import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { masterAPI } from '../services/api';
import { toast } from 'react-toastify';
import { FiPlus, FiEdit2, FiX, FiList, FiToggleLeft, FiToggleRight, FiSearch } from 'react-icons/fi';
import { useAuth } from '../context/AuthContext';

const Masters = ({ tab = 'tradegroups' }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState(tab);

  // Update active tab when route changes
  useEffect(() => {
    const path = location.pathname;
    if (path.includes('/tradegroups')) setActiveTab('tradegroups');
    else if (path.includes('/trades')) setActiveTab('trades');
    else if (path.includes('/coursetypes')) setActiveTab('coursetypes');
    else if (path.includes('/subjects')) setActiveTab('subjects');
    else if (path.includes('/chapters')) setActiveTab('chapters');
    else if (path.includes('/topics')) setActiveTab('topics');
  }, [location]);

  const handleTabChange = (tabName) => {
    setActiveTab(tabName);
    navigate(`/masters/${tabName}`);
  };
  
  return (
    <div className="p-4 md:p-6">
      <div className="flex justify-between items-start mb-6 md:mb-8">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Master Data</h1>
          <p className="text-sm md:text-base text-gray-600 mt-1">Manage shared master data (visible to all institutes)</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 md:gap-2 mb-4 md:mb-6 border-b-2 border-gray-200 overflow-x-auto hide-scrollbar">
        <button
          className={`flex items-center gap-1 md:gap-2 px-3 md:px-6 py-2 md:py-3 font-medium text-xs md:text-sm transition-all duration-200 border-b-3 -mb-0.5 whitespace-nowrap min-h-[44px] ${
            activeTab === 'tradegroups'
              ? 'text-primary-600 border-b-primary-600'
              : 'text-gray-600 border-b-transparent hover:text-primary-600'
          }`}
          onClick={() => handleTabChange('tradegroups')}
        >
          <FiList className="md:inline" /> <span className="hidden sm:inline">Categories</span><span className="sm:hidden">Categories</span>
        </button>
        <button
          className={`flex items-center gap-1 md:gap-2 px-3 md:px-6 py-2 md:py-3 font-medium text-xs md:text-sm transition-all duration-200 border-b-3 -mb-0.5 whitespace-nowrap min-h-[44px] ${
            activeTab === 'trades'
              ? 'text-primary-600 border-b-primary-600'
              : 'text-gray-600 border-b-transparent hover:text-primary-600'
          }`}
          onClick={() => handleTabChange('trades')}
        >
          <FiList className="md:inline" /> Trades
        </button>
        <button
          className={`flex items-center gap-1 md:gap-2 px-3 md:px-6 py-2 md:py-3 font-medium text-xs md:text-sm transition-all duration-200 border-b-3 -mb-0.5 whitespace-nowrap min-h-[44px] ${
            activeTab === 'subjects'
              ? 'text-primary-600 border-b-primary-600'
              : 'text-gray-600 border-b-transparent hover:text-primary-600'
          }`}
          onClick={() => handleTabChange('subjects')}
        >
          <FiList className="md:inline" /> Subjects
        </button>
        <button
          className={`flex items-center gap-1 md:gap-2 px-3 md:px-6 py-2 md:py-3 font-medium text-xs md:text-sm transition-all duration-200 border-b-3 -mb-0.5 whitespace-nowrap min-h-[44px] ${
            activeTab === 'chapters'
              ? 'text-primary-600 border-b-primary-600'
              : 'text-gray-600 border-b-transparent hover:text-primary-600'
          }`}
          onClick={() => handleTabChange('chapters')}
        >
          <FiList className="md:inline" /> Chapters
        </button>
       
        <button
          className={`flex items-center gap-1 md:gap-2 px-3 md:px-6 py-2 md:py-3 font-medium text-xs md:text-sm transition-all duration-200 border-b-3 -mb-0.5 whitespace-nowrap min-h-[44px] ${
            activeTab === 'topics'
              ? 'text-primary-600 border-b-primary-600'
              : 'text-gray-600 border-b-transparent hover:text-primary-600'
          }`}
          onClick={() => handleTabChange('topics')}
        >
          <FiList className="md:inline" /> Topics
        </button>
      </div>

      {/* Content */}
      <div className="bg-white rounded-xl shadow-sm p-4 md:p-6">
        {activeTab === 'tradegroups' && <TradeGroupsTab />}
        {activeTab === 'trades' && <TradesTab />}
        {activeTab === 'coursetypes' && <ComingSoonTab title="Course Types" />}
        {activeTab === 'subjects' && <SubjectsTab />}
        {activeTab === 'chapters' && <ChaptersTab />}
        {activeTab === 'topics' && <TopicsTab />}
      </div>
    </div>
  );
};

// Coming Soon Tab Component for future features
const ComingSoonTab = ({ title }) => {
  return (
    <div className="text-center py-16">
      <FiList size={64} className="mx-auto text-gray-300 mb-4" />
      <h3 className="text-2xl font-semibold text-gray-900 mb-2">{title}</h3>
      <p className="text-gray-600 mb-6">This feature is coming soon!</p>
      <p className="text-sm text-gray-500">
        You can manage {title.toLowerCase()} from the Super Admin Laravel panel for now.
      </p>
    </div>
  );
};

// Categorys Tab Component
const TradeGroupsTab = () => {
  const { user } = useAuth();
  const [tradegroups, setTradeGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({ name: '' });
  const [submitting, setSubmitting] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchTradeGroups();
  }, []);

  const fetchTradeGroups = async () => {
    try {
      const response = await masterAPI.getTradeGroups();
      if (response.data.success) {
        setTradeGroups(response.data.data);
      }
    } catch (error) {
      toast.error('Error fetching Categorys');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      let response;
      if (editingItem) {
        response = await masterAPI.updateTradeGroup(editingItem.id, formData);
      } else {
        response = await masterAPI.createTradeGroup(formData);
      }

      if (response.data.success) {
        toast.success(response.data.message);
        setShowModal(false);
        setEditingItem(null);
        setFormData({ name: '' });
        fetchTradeGroups();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Operation failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({ name: item.name });
    setShowModal(true);
  };

  const handleToggleStatus = async (item) => {
    const action = item.status === 1 ? 'disable' : 'enable';
    if (!window.confirm(`Are you sure you want to ${action} "${item.name}"?`)) {
      return;
    }

    try {
      const response = await masterAPI.disableTradeGroup(item.id);
      if (response.data.success) {
        toast.success(response.data.message);
        fetchTradeGroups();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || `Error ${action}ing Category`);
    }
  };

  const handleAddNew = () => {
    setEditingItem(null);
    setFormData({ name: '' });
    setShowModal(true);
  };

  const canEdit = (item) => {
    return item.institute_id && item.institute_id === user?.institute_id;
  };

  // Filter Categorys based on search query
  const filteredTradeGroups = useMemo(() => {
    if (!searchQuery.trim()) return tradegroups;
    
    const query = searchQuery.toLowerCase();
    return tradegroups.filter(item => {
      const name = item.name?.toLowerCase() || '';
      const createdBy = item.created_by_institute?.toLowerCase() || '';
      
      return name.includes(query) || createdBy.includes(query);
    });
  }, [tradegroups, searchQuery]);

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p className="mt-4 text-gray-600">Loading Categorys...</p>
      </div>
    );
  }

  return (
    <div className="w-full">
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-xl font-semibold text-gray-900">Categories</h3>
        <button 
          className="flex items-center gap-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-lg transition-colors" 
          onClick={handleAddNew}
        >
          <FiPlus /> Add Category
        </button>
      </div>

      {/* Search Bar */}
      <div className="mb-6">
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <FiSearch className="h-5 w-5 text-gray-400" />
          </div>
          <input
            type="text"
            placeholder="Search by name or creator..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-2 focus:ring-primary-600 focus:border-transparent text-sm md:text-base"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute inset-y-0 right-0 pr-3 flex items-center"
            >
              <FiX className="h-5 w-5 text-gray-400 hover:text-gray-600" />
            </button>
          )}
        </div>
        {searchQuery && (
          <p className="mt-2 text-sm text-gray-600">
            Found {filteredTradeGroups.length} {filteredTradeGroups.length === 1 ? 'category' : 'categories'}
          </p>
        )}
      </div>

      <div className="p-4 mb-5 bg-blue-50 border-l-4 border-blue-500 rounded-lg">
        <p className="text-sm text-blue-900">
          <strong>Note:</strong> Categories created by any institute are visible and usable by all institutes. 
          You can only edit/disable items created by your institute.
        </p>
      </div>

      {tradegroups.length === 0 ? (
        <div className="text-center py-16">
          <FiList size={48} className="mx-auto text-gray-300 mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No categories yet</h3>
          <p className="text-gray-600 mb-6">Create your first category to organize trades</p>
          <button 
            className="flex items-center gap-2 px-6 py-3 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-lg transition-colors" 
            onClick={handleAddNew}
          >
            <FiPlus /> Create Category
          </button>
        </div>
      ) : filteredTradeGroups.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 bg-gray-50 rounded-lg">
          <FiSearch size={48} className="text-gray-300 mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No results found</h3>
          <p className="text-sm text-gray-500 mb-6">
            No categories match "{searchQuery}". Try a different search term.
          </p>
          <button
            onClick={() => setSearchQuery('')}
            className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors"
          >
            Clear Search
          </button>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">#</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Name</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Created By</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Trades</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Status</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredTradeGroups.map((item, index) => (
                <tr 
                  key={item.id} 
                  className={`border-b border-gray-100 hover:bg-gray-50 transition-colors ${
                    item.status === 0 ? 'opacity-50' : ''
                  }`}
                >
                  <td className="px-4 py-3 text-sm text-gray-600">{index + 1}</td>
                  <td className="px-4 py-3 text-sm text-gray-900">{item.name}</td>
                  <td className="px-4 py-3">
                    <span className="inline-block px-3 py-1 text-xs font-medium bg-purple-100 text-purple-800 rounded-full">
                      {item.created_by_institute}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">{item.trades_count || 0}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-block px-3 py-1 text-xs font-medium rounded-full ${
                      item.status === 1 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {item.status_label}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    {canEdit(item) ? (
                      <div className="flex justify-end gap-1">
                        <button className="btn-icon" onClick={() => handleEdit(item)} title="Edit">
                          <FiEdit2 />
                        </button>
                        <button 
                          className={`btn-icon ${item.status == 1 ? 'danger' : 'success'}`}
                          onClick={() => handleToggleStatus(item)}
                          title={item.status == 1 ? 'Disable' : 'Enable'}
                        >
                          {item.status === 1 ? <FiToggleRight size={20} /> : <FiToggleLeft size={20} />}
                        </button>
                      </div>
                    ) : (
                      <span className="text-xs text-gray-400 italic">View Only</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-5" onClick={() => setShowModal(false)}>
          <div className="bg-white rounded-xl max-w-md w-full max-h-[90vh] flex flex-col shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="px-8 py-6 border-b border-gray-200 flex justify-between items-center">
              <h2 className="text-2xl font-bold text-gray-900">{editingItem ? 'Edit' : 'Add'}  Categories</h2>
              <button className="p-1 hover:bg-gray-100 rounded-md transition-colors" onClick={() => setShowModal(false)}>
                <FiX size={24} className="text-gray-600" />
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="px-8 py-6 overflow-y-auto flex-1">
                <div className="mb-5">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Categories Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ name: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600 focus:border-transparent outline-none transition-colors"
                    placeholder="Enter categories name"
                    required
                    autoFocus
                  />
                  <small className="block mt-2 text-xs text-gray-500 italic">
                    This will be shared with all institutes
                  </small>
                </div>
              </div>
              <div className="px-8 py-5 border-t border-gray-200 flex justify-end gap-3">
                <button
                  type="button"
                  className="flex-1 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition-colors"
                  onClick={() => setShowModal(false)}
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-lg transition-colors"
                  disabled={submitting}
                >
                  {submitting ? 'Saving...' : editingItem ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

// Trades Tab Component
const TradesTab = () => {
  const { user } = useAuth();
  const [trades, setTrades] = useState([]);
  const [tradegroups, setTradeGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({ name: '', tradegroup: '' });
  const [submitting, setSubmitting] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [tradesRes, tradegroupsRes] = await Promise.all([
        masterAPI.getTrades(),
        masterAPI.getTradeGroups()
      ]);
      
      if (tradesRes.data.success) {
        setTrades(tradesRes.data.data);
      }
      if (tradegroupsRes.data.success) {
        setTradeGroups(tradegroupsRes.data.data.filter(tg => tg.status === 1));
      }
    } catch (error) {
      toast.error('Error fetching data');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      let response;
      if (editingItem) {
        response = await masterAPI.updateTrade(editingItem.id, formData);
      } else {
        response = await masterAPI.createTrade(formData);
      }

      if (response.data.success) {
        toast.success(response.data.message);
        setShowModal(false);
        setEditingItem(null);
        setFormData({ name: '', tradegroup: '' });
        fetchData();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Operation failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({ name: item.name, tradegroup: item.tradegroup });
    setShowModal(true);
  };

  const handleToggleStatus = async (item) => {
    const action = item.status === 1 ? 'disable' : 'enable';
    if (!window.confirm(`Are you sure you want to ${action} "${item.name}"?`)) {
      return;
    }

    try {
      const response = await masterAPI.disableTrade(item.id);
      if (response.data.success) {
        toast.success(response.data.message);
        fetchData();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || `Error ${action}ing trade`);
    }
  };

  const handleAddNew = () => {
    setEditingItem(null);
    setFormData({ name: '', tradegroup_id: '' });
    setShowModal(true);
  };

  const canEdit = (item) => {
    return item.institute_id && item.institute_id === user?.institute_id;
  };

  // Filter trades based on search query
  const filteredTrades = useMemo(() => {
    if (!searchQuery.trim()) return trades;
    
    const query = searchQuery.toLowerCase();
    return trades.filter(item => {
      const name = item.name?.toLowerCase() || '';
      const tradegroupName = item.tradegroup_name?.toLowerCase() || '';
      const createdBy = item.created_by_institute?.toLowerCase() || '';
      
      return name.includes(query) || tradegroupName.includes(query) || createdBy.includes(query);
    });
  }, [trades, searchQuery]);

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p className="mt-4 text-gray-600">Loading trades...</p>
      </div>
    );
  }

  return (
    <div className="w-full">
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-xl font-semibold text-gray-900">Sub Categories</h3>
        <button 
          className="flex items-center gap-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-lg transition-colors" 
          onClick={handleAddNew}
        >
          <FiPlus /> Add Sub Category
        </button>
      </div>

      {/* Search Bar */}
      <div className="mb-6">
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <FiSearch className="h-5 w-5 text-gray-400" />
          </div>
          <input
            type="text"
            placeholder="Search bySub Category, category, or creator..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-2 focus:ring-primary-600 focus:border-transparent text-sm md:text-base"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute inset-y-0 right-0 pr-3 flex items-center"
            >
              <FiX className="h-5 w-5 text-gray-400 hover:text-gray-600" />
            </button>
          )}
        </div>
        {searchQuery && (
          <p className="mt-2 text-sm text-gray-600">
            Found {filteredTrades.length} {filteredTrades.length === 1 ? 'trade' : 'trades'}
          </p>
        )}
      </div>

      <div className="p-4 mb-5 bg-blue-50 border-l-4 border-blue-500 rounded-lg">
        <p className="text-sm text-blue-900">
          <strong>Note:</strong> Trades created by any institute are visible and usable by all institutes. 
          You can only edit/disable items created by your institute.
        </p>
      </div>

      {trades.length === 0 ? (
        <div className="text-center py-16">
          <FiList size={48} className="mx-auto text-gray-300 mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No trades yet</h3>
          <p className="text-gray-600 mb-6">Create your first Subcategory under a category</p>
          <button 
            className="flex items-center gap-2 px-6 py-3 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-lg transition-colors" 
            onClick={handleAddNew}
          >
            <FiPlus /> Create Trade
          </button>
        </div>
      ) : filteredTrades.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 bg-gray-50 rounded-lg">
          <FiSearch size={48} className="text-gray-300 mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No results found</h3>
          <p className="text-sm text-gray-500 mb-6">
            No trades match "{searchQuery}". Try a different search term.
          </p>
          <button
            onClick={() => setSearchQuery('')}
            className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors"
          >
            Clear Search
          </button>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">#</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Sub Category Name</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Category</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Created By</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Status</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredTrades.map((item, index) => (
                <tr 
                  key={item.id} 
                  className={`border-b border-gray-100 hover:bg-gray-50 transition-colors ${
                    item.status === 0 ? 'opacity-50' : ''
                  }`}
                >
                  <td className="px-4 py-3 text-sm text-gray-600">{index + 1}</td>
                  <td className="px-4 py-3 text-sm text-gray-900">{item.name}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{item.tradegroup_name || 'N/A'}</td>
                  <td className="px-4 py-3">
                    <span className="inline-block px-3 py-1 text-xs font-medium bg-purple-100 text-purple-800 rounded-full">
                      {item.created_by_institute}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-block px-3 py-1 text-xs font-medium rounded-full ${
                      item.status === 1 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {item.status_label}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    {canEdit(item) ? (
                      <div className="flex justify-end gap-1">
                        <button className="btn-icon" onClick={() => handleEdit(item)} title="Edit">
                          <FiEdit2 />
                        </button>
                        <button 
                          className={`btn-icon ${item.status == 1 ? 'danger' : 'success'}`}
                          onClick={() => handleToggleStatus(item)}
                          title={item.status == 1 ? 'Disable' : 'Enable'}
                        >
                          {item.status === 1 ? <FiToggleRight size={20} /> : <FiToggleLeft size={20} />}
                        </button>
                      </div>
                    ) : (
                      <span className="text-xs text-gray-400 italic">View Only</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-5" onClick={() => setShowModal(false)}>
          <div className="bg-white rounded-xl max-w-md w-full max-h-[90vh] flex flex-col shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="px-8 py-6 border-b border-gray-200 flex justify-between items-center">
              <h2 className="text-2xl font-bold text-gray-900">{editingItem ? 'Edit' : 'Add'} Sub Categories</h2>
              <button className="p-1 hover:bg-gray-100 rounded-md transition-colors" onClick={() => setShowModal(false)}>
                <FiX size={24} className="text-gray-600" />
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="px-8 py-6 overflow-y-auto flex-1">
                <div className="mb-5">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Category <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="tradegroup"
                    value={formData.tradegroup}
                    onChange={(e) => setFormData({ ...formData, tradegroup: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600 focus:border-transparent outline-none transition-colors bg-white"
                    required
                  >
                    <option value="">Select Category</option>
                    {tradegroups.map((tg) => (
                      <option key={tg.id} value={tg.id}>
                        {tg.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="mb-5">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                   Sub Category <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600 focus:border-transparent outline-none transition-colors"
                    placeholder="Enter subcategories name"
                    required
                  />
                  <small className="block mt-2 text-xs text-gray-500 italic">
                    This will be shared with all institutes
                  </small>
                </div>
              </div>
              <div className="px-8 py-2 border-t border-gray-200 flex justify-end gap-3">
                <button
                  type="button"
                  className="flex-1 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition-colors"
                  onClick={() => setShowModal(false)}
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-lg transition-colors"
                  disabled={submitting}
                >
                  {submitting ? 'Saving...' : editingItem ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

// Subjects Tab Component
const SubjectsTab = () => {
  const { user } = useAuth();
  const [subjects, setSubjects] = useState([]);
  const [tradeGroups, setTradeGroups] = useState([]);
  const [availableTrades, setAvailableTrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({ name: '', tradegroup: '', trade: '' });
  const [submitting, setSubmitting] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchSubjects();
    fetchTradeGroups();
  }, []);

  const fetchSubjects = async () => {
    setLoading(true);
    try {
      const response = await masterAPI.getSubjects();
      if (response.data.success) {
        setSubjects(response.data.data);
      }
    } catch (error) {
      toast.error('Error fetching subjects');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTradeGroups = async () => {
    try {
      const response = await masterAPI.getTradeGroups();
      if (response.data.success) {
        setTradeGroups(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching Categorys:', error);
    }
  };

  const handleTradeGroupChange = async (e) => {
    const tradegroupId = e.target.value;
    setFormData({ ...formData, tradegroup: tradegroupId, trade: '' });
    
    if (tradegroupId) {
      try {
        const response = await masterAPI.getTradesByGroup(tradegroupId);
        if (response.data.success) {
          setAvailableTrades(response.data.data);
        }
      } catch (error) {
        console.error('Error fetching trades:', error);
        setAvailableTrades([]);
      }
    } else {
      setAvailableTrades([]);
    }
  };

  const handleAddNew = () => {
    setEditingItem(null);
    setFormData({ name: '', tradegroup: '', trade: '' });
    setAvailableTrades([]);
    setShowModal(true);
  };

  const handleEdit = async (item) => {
    setEditingItem(item);
    setFormData({ 
      name: item.name, 
      tradegroup: item.tradegroup || '', 
      trade: item.trade || '' 
    });
    
    // Load trades for the selected Category
    if (item.tradegroup) {
      try {
        const response = await masterAPI.getTradesByGroup(item.tradegroup);
        if (response.data.success) {
          setAvailableTrades(response.data.data);
        }
      } catch (error) {
        console.error('Error fetching trades:', error);
      }
    }
    
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      let response;
      if (editingItem) {
        response = await masterAPI.updateSubject(editingItem.id, formData);
      } else {
        response = await masterAPI.createSubject(formData);
      }

      if (response.data.success) {
        toast.success(response.data.message);
        setShowModal(false);
        setFormData({ name: '', tradegroup: '', trade: '' });
        setAvailableTrades([]);
        fetchSubjects();
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Operation failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleStatus = async (item) => {
    const action = item.status === 1 ? 'disable' : 'enable';
    if (!window.confirm(`Are you sure you want to ${action} "${item.name}"?`)) {
      return;
    }

    try {
      const response = await masterAPI.disableSubject(item.id);
      if (response.data.success) {
        toast.success(response.data.message);
        fetchSubjects();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Operation failed');
    }
  };

  // Filter subjects based on search query
  const filteredSubjects = useMemo(() => {
    if (!searchQuery.trim()) return subjects;
    
    const query = searchQuery.toLowerCase();
    return subjects.filter(item => {
      const name = item.name?.toLowerCase() || '';
      const tradegroupName = item.tradegroup_name?.toLowerCase() || '';
      const tradeName = item.trade_name?.toLowerCase() || '';
      const createdBy = item.created_by_institute?.toLowerCase() || '';
      
      return name.includes(query) || tradegroupName.includes(query) || 
             tradeName.includes(query) || createdBy.includes(query);
    });
  }, [subjects, searchQuery]);

  if (loading) {
    return (
      <div className="flex justify-center items-center py-16">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="w-full">
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-xl font-semibold text-gray-900">Subjects</h3>
        <button 
          className="flex items-center gap-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-lg transition-colors" 
          onClick={handleAddNew}
        >
          <FiPlus /> Add Subject
        </button>
      </div>

      {/* Search Bar */}
      <div className="mb-6">
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <FiSearch className="h-5 w-5 text-gray-400" />
          </div>
          <input
            type="text"
            placeholder="Search by name, category, trade, or creator..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-2 focus:ring-primary-600 focus:border-transparent text-sm md:text-base"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute inset-y-0 right-0 pr-3 flex items-center"
            >
              <FiX className="h-5 w-5 text-gray-400 hover:text-gray-600" />
            </button>
          )}
        </div>
        {searchQuery && (
          <p className="mt-2 text-sm text-gray-600">
            Found {filteredSubjects.length} {filteredSubjects.length === 1 ? 'subject' : 'subjects'}
          </p>
        )}
      </div>

      <div className="p-4 mb-5 bg-blue-50 border-l-4 border-blue-500 rounded-lg">
        <p className="text-sm text-blue-900">
          <strong>Note:</strong> Subjects created by any institute are visible and usable by all institutes. 
          You can only edit/disable items created by your institute.
        </p>
      </div>

      {subjects.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <FiList size={64} className="text-gray-300 mb-4" />
          <h3 className="text-xl font-semibold text-gray-700 mb-2">No subjects yet</h3>
          <p className="text-gray-500 mb-6">Create your first subject to get started</p>
          <button 
            className="flex items-center gap-2 px-6 py-3 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-lg transition-colors" 
            onClick={handleAddNew}
          >
            <FiPlus /> Create Subject
          </button>
        </div>
      ) : filteredSubjects.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 bg-gray-50 rounded-lg">
          <FiSearch size={48} className="text-gray-300 mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No results found</h3>
          <p className="text-sm text-gray-500 mb-6">
            No subjects match "{searchQuery}". Try a different search term.
          </p>
          <button
            onClick={() => setSearchQuery('')}
            className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors"
          >
            Clear Search
          </button>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b-2 border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Category</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Trade</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Name</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Created By</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Status</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredSubjects.map((item) => {
                const canEdit = item.institute_id === user.institute_id || item.institute_id === null;
                const isDisabled = item.status === 0;
                
                return (
                  <tr 
                    key={item.id}
                    className={`border-b border-gray-100 hover:bg-gray-50 transition-colors ${
                      isDisabled ? 'opacity-50' : ''
                    }`}
                  >
                    <td className="px-4 py-3 text-sm text-gray-600">{item.tradegroup_name || 'N/A'}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{item.trade_name || 'N/A'}</td>
                    <td className="px-4 py-3">
                      <span className="text-sm font-medium text-gray-900">{item.name}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs text-gray-600">{item.created_by_institute}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-block px-3 py-1 text-xs font-medium rounded-full ${
                        item.status === 1 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {item.status_label}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end items-center gap-2">
                        {canEdit && item.institute_id === user.institute_id && (
                          <>
                            <button
                              onClick={() => handleEdit(item)}
                              className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                              title="Edit"
                            >
                              <FiEdit2 size={16} />
                            </button>
                            <button
                              onClick={() => handleToggleStatus(item)}
                              className={`p-2 rounded-lg transition-colors ${
                                item.status === 1 
                                  ? 'text-red-600 hover:bg-red-50' 
                                  : 'text-green-600 hover:bg-green-50'
                              }`}
                              title={item.status === 1 ? 'Disable' : 'Enable'}
                            >
                              {item.status === 1 ? <FiToggleRight size={20} /> : <FiToggleLeft size={20} />}
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-900">
                {editingItem ? 'Edit Subject' : 'Add New Subject'}
              </h2>
              <button
                onClick={() => setShowModal(false)}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <FiX size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6">
              <div className="mb-5">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Category Name <span className="text-red-500">*</span>
                </label>
                <select
                  name="tradegroup"
                  value={formData.tradegroup}
                  onChange={handleTradeGroupChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600 focus:border-transparent outline-none transition-colors"
                  required
                >
                  <option value="">Select Category</option>
                  {tradeGroups.map((tg) => (
                    <option key={tg.id} value={tg.id}>{tg.name}</option>
                  ))}
                </select>
              </div>

              <div className="mb-5">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                 Sub Category <span className="text-red-500">*</span>
                </label>
                <select
                  name="trade"
                  value={formData.trade}
                  onChange={(e) => setFormData({ ...formData, trade: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600 focus:border-transparent outline-none transition-colors"
                  required
                  disabled={!formData.tradegroup}
                >
                  <option value="">Select Trade</option>
                  {availableTrades.map((t) => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </select>
              </div>

              <div className="mb-5">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Subject Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600 focus:border-transparent outline-none transition-colors"
                  placeholder="Enter subject name"
                  required
                />
                <small className="block mt-2 text-xs text-gray-500 italic">
                  This will be shared with all institutes
                </small>
              </div>

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="flex-1 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition-colors"
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-lg transition-colors"
                  disabled={submitting}
                >
                  {submitting ? 'Saving...' : (editingItem ? 'Update' : 'Create')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

// Chapters Tab Component
const ChaptersTab = () => {
  const { user } = useAuth();
  const [chapters, setChapters] = useState([]);
  const [tradeGroups, setTradeGroups] = useState([]);
  const [availableTrades, setAvailableTrades] = useState([]);
  const [availableSubjects, setAvailableSubjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({ name: '', tradegroup: '', trade: '', subject: '' });
  const [submitting, setSubmitting] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchChapters();
    fetchTradeGroups();
  }, []);

  const fetchChapters = async () => {
    setLoading(true);
    try {
      const response = await masterAPI.getChapters();
      if (response.data.success) {
        setChapters(response.data.data);
      }
    } catch (error) {
      toast.error('Error fetching chapters');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTradeGroups = async () => {
    try {
      const response = await masterAPI.getTradeGroups();
      if (response.data.success) {
        setTradeGroups(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching Categorys:', error);
    }
  };

  const handleTradeGroupChange = async (e) => {
    const tradegroupId = e.target.value;
    setFormData({ ...formData, tradegroup: tradegroupId, trade: '', subject: '' });
    setAvailableSubjects([]);
    
    if (tradegroupId) {
      try {
        const response = await masterAPI.getTradesByGroup(tradegroupId);
        if (response.data.success) {
          setAvailableTrades(response.data.data);
        }
      } catch (error) {
        console.error('Error fetching trades:', error);
        setAvailableTrades([]);
      }
    } else {
      setAvailableTrades([]);
    }
  };

  const handleTradeChange = async (e) => {
    const tradeId = e.target.value;
    setFormData({ ...formData, trade: tradeId, subject: '' });
    
    if (tradeId && formData.tradegroup) {
      try {
        const response = await masterAPI.getSubjectsByTrade(formData.tradegroup, tradeId);
        if (response.data.success) {
          setAvailableSubjects(response.data.data);
        }
      } catch (error) {
        console.error('Error fetching subjects:', error);
        setAvailableSubjects([]);
      }
    } else {
      setAvailableSubjects([]);
    }
  };

  const handleAddNew = () => {
    setEditingItem(null);
    setFormData({ name: '', tradegroup: '', trade: '', subject: '' });
    setAvailableTrades([]);
    setAvailableSubjects([]);
    setShowModal(true);
  };

  const handleEdit = async (item) => {
    setEditingItem(item);
    setFormData({ 
      name: item.name, 
      tradegroup: item.tradegroup || '', 
      trade: item.trade || '',
      subject: item.subject || ''
    });
    
    if (item.tradegroup) {
      try {
        const tradesRes = await masterAPI.getTradesByGroup(item.tradegroup);
        if (tradesRes.data.success) {
          setAvailableTrades(tradesRes.data.data);
        }
        
        if (item.trade) {
          const subjectsRes = await masterAPI.getSubjectsByTrade(item.tradegroup, item.trade);
          if (subjectsRes.data.success) {
            setAvailableSubjects(subjectsRes.data.data);
          }
        }
      } catch (error) {
        console.error('Error loading data:', error);
      }
    }
    
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      let response;
      if (editingItem) {
        response = await masterAPI.updateChapter(editingItem.id, formData);
      } else {
        response = await masterAPI.createChapter(formData);
      }

      if (response.data.success) {
        toast.success(response.data.message);
        setShowModal(false);
        setFormData({ name: '', tradegroup: '', trade: '', subject: '' });
        setAvailableTrades([]);
        setAvailableSubjects([]);
        fetchChapters();
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Operation failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleStatus = async (item) => {
    const action = item.status === 1 ? 'disable' : 'enable';
    if (!window.confirm(`Are you sure you want to ${action} "${item.name}"?`)) {
      return;
    }

    try {
      const response = await masterAPI.disableChapter(item.id);
      if (response.data.success) {
        toast.success(response.data.message);
        fetchChapters();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Operation failed');
    }
  };

  // Filter chapters based on search query
  const filteredChapters = useMemo(() => {
    if (!searchQuery.trim()) return chapters;
    
    const query = searchQuery.toLowerCase();
    return chapters.filter(item => {
      const name = item.name?.toLowerCase() || '';
      const tradegroupName = item.tradegroup_name?.toLowerCase() || '';
      const tradeName = item.trade_name?.toLowerCase() || '';
      const subjectName = item.subject_name?.toLowerCase() || '';
      const createdBy = item.created_by_institute?.toLowerCase() || '';
      
      return name.includes(query) || tradegroupName.includes(query) || 
             tradeName.includes(query) || subjectName.includes(query) || 
             createdBy.includes(query);
    });
  }, [chapters, searchQuery]);

  if (loading) {
    return (
      <div className="flex justify-center items-center py-16">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="w-full">
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-xl font-semibold text-gray-900">Chapters</h3>
        <button 
          className="flex items-center gap-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-lg transition-colors" 
          onClick={handleAddNew}
        >
          <FiPlus /> Add Chapter
        </button>
      </div>

      {/* Search Bar */}
      <div className="mb-6">
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <FiSearch className="h-5 w-5 text-gray-400" />
          </div>
          <input
            type="text"
            placeholder="Search by name, category, trade, subject, or creator..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-2 focus:ring-primary-600 focus:border-transparent text-sm md:text-base"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute inset-y-0 right-0 pr-3 flex items-center"
            >
              <FiX className="h-5 w-5 text-gray-400 hover:text-gray-600" />
            </button>
          )}
        </div>
        {searchQuery && (
          <p className="mt-2 text-sm text-gray-600">
            Found {filteredChapters.length} {filteredChapters.length === 1 ? 'chapter' : 'chapters'}
          </p>
        )}
      </div>

      <div className="p-4 mb-5 bg-blue-50 border-l-4 border-blue-500 rounded-lg">
        <p className="text-sm text-blue-900">
          <strong>Note:</strong> Chapters created by any institute are visible and usable by all institutes. 
          You can only edit/disable items created by your institute.
        </p>
      </div>

      {chapters.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <FiList size={64} className="text-gray-300 mb-4" />
          <h3 className="text-xl font-semibold text-gray-700 mb-2">No chapters yet</h3>
          <p className="text-gray-500 mb-6">Create your first chapter to get started</p>
          <button 
            className="flex items-center gap-2 px-6 py-3 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-lg transition-colors" 
            onClick={handleAddNew}
          >
            <FiPlus /> Create Chapter
          </button>
        </div>
      ) : filteredChapters.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 bg-gray-50 rounded-lg">
          <FiSearch size={48} className="text-gray-300 mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No results found</h3>
          <p className="text-sm text-gray-500 mb-6">
            No chapters match "{searchQuery}". Try a different search term.
          </p>
          <button
            onClick={() => setSearchQuery('')}
            className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors"
          >
            Clear Search
          </button>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b-2 border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Category</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Trade</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Subject</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Name</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Created By</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Status</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredChapters.map((item) => {
                const canEdit = item.institute_id === user.institute_id || item.institute_id === null;
                const isDisabled = item.status === 0;
                
                return (
                  <tr 
                    key={item.id}
                    className={`border-b border-gray-100 hover:bg-gray-50 transition-colors ${
                      isDisabled ? 'opacity-50' : ''
                    }`}
                  >
                    <td className="px-4 py-3 text-sm text-gray-600">{item.tradegroup_name || 'N/A'}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{item.trade_name || 'N/A'}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{item.subject_name || 'N/A'}</td>
                    <td className="px-4 py-3">
                      <span className="text-sm font-medium text-gray-900">{item.name}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs text-gray-600">{item.created_by_institute}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-block px-3 py-1 text-xs font-medium rounded-full ${
                        item.status === 1 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {item.status_label}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end items-center gap-2">
                        {canEdit && item.institute_id === user.institute_id && (
                          <>
                            <button
                              onClick={() => handleEdit(item)}
                              className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                              title="Edit"
                            >
                              <FiEdit2 size={16} />
                            </button>
                            <button
                              onClick={() => handleToggleStatus(item)}
                              className={`p-2 rounded-lg transition-colors ${
                                item.status === 1 
                                  ? 'text-red-600 hover:bg-red-50' 
                                  : 'text-green-600 hover:bg-green-50'
                              }`}
                              title={item.status === 1 ? 'Disable' : 'Enable'}
                            >
                              {item.status === 1 ? <FiToggleRight size={20} /> : <FiToggleLeft size={20} />}
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-900">
                {editingItem ? 'Edit Chapter' : 'Add New Chapter'}
              </h2>
              <button
                onClick={() => setShowModal(false)}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <FiX size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6">
              <div className="mb-5">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Category <span className="text-red-500">*</span>
                </label>
                <select
                  name="tradegroup"
                  value={formData.tradegroup}
                  onChange={handleTradeGroupChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600 focus:border-transparent outline-none transition-colors"
                  required
                >
                  <option value="">Select Category</option>
                  {tradeGroups.map((tg) => (
                    <option key={tg.id} value={tg.id}>{tg.name}</option>
                  ))}
                </select>
              </div>

              <div className="mb-5">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                 Sub Category<span className="text-red-500">*</span>
                </label>
                <select
                  name="trade"
                  value={formData.trade}
                  onChange={handleTradeChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600 focus:border-transparent outline-none transition-colors"
                  required
                  disabled={!formData.tradegroup}
                >
                  <option value="">Select Trade</option>
                  {availableTrades.map((t) => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </select>
              </div>

              <div className="mb-5">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Subject <span className="text-red-500">*</span>
                </label>
                <select
                  name="subject"
                  value={formData.subject}
                  onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600 focus:border-transparent outline-none transition-colors"
                  required
                  disabled={!formData.trade}
                >
                  <option value="">Select Subject</option>
                  {availableSubjects.map((s) => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
              </div>

              <div className="mb-5">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Chapter Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600 focus:border-transparent outline-none transition-colors"
                  placeholder="Enter chapter name"
                  required
                />
                <small className="block mt-2 text-xs text-gray-500 italic">
                  This will be shared with all institutes
                </small>
              </div>

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="flex-1 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition-colors"
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-lg transition-colors"
                  disabled={submitting}
                >
                  {submitting ? 'Saving...' : (editingItem ? 'Update' : 'Create')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

// Topics Tab Component  
const TopicsTab = () => {
  const { user } = useAuth();
  const [topics, setTopics] = useState([]);
  const [tradeGroups, setTradeGroups] = useState([]);
  const [availableTrades, setAvailableTrades] = useState([]);
  const [availableSubjects, setAvailableSubjects] = useState([]);
  const [availableChapters, setAvailableChapters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({ name: '', tradegroup: '', trade: '', subject: '', chapter: '' });
  const [submitting, setSubmitting] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchTopics();
    fetchTradeGroups();
  }, []);

  const fetchTopics = async () => {
    setLoading(true);
    try {
      const response = await masterAPI.getTopics();
      if (response.data.success) {
        setTopics(response.data.data);
      }
    } catch (error) {
      toast.error('Error fetching topics');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTradeGroups = async () => {
    try {
      const response = await masterAPI.getTradeGroups();
      if (response.data.success) {
        setTradeGroups(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching Categorys:', error);
    }
  };

  const handleTradeGroupChange = async (e) => {
    const tradegroupId = e.target.value;
    setFormData({ ...formData, tradegroup: tradegroupId, trade: '', subject: '', chapter: '' });
    setAvailableSubjects([]);
    setAvailableChapters([]);
    
    if (tradegroupId) {
      try {
        const response = await masterAPI.getTradesByGroup(tradegroupId);
        if (response.data.success) {
          setAvailableTrades(response.data.data);
        }
      } catch (error) {
        console.error('Error fetching trades:', error);
        setAvailableTrades([]);
      }
    } else {
      setAvailableTrades([]);
    }
  };

  const handleTradeChange = async (e) => {
    const tradeId = e.target.value;
    setFormData({ ...formData, trade: tradeId, subject: '', chapter: '' });
    setAvailableChapters([]);
    
    if (tradeId && formData.tradegroup) {
      try {
        const response = await masterAPI.getSubjectsByTrade(formData.tradegroup, tradeId);
        if (response.data.success) {
          setAvailableSubjects(response.data.data);
        }
      } catch (error) {
        console.error('Error fetching subjects:', error);
        setAvailableSubjects([]);
      }
    } else {
      setAvailableSubjects([]);
    }
  };

  const handleSubjectChange = async (e) => {
    const subjectId = e.target.value;
    setFormData({ ...formData, subject: subjectId, chapter: '' });
    
    if (subjectId && formData.tradegroup && formData.trade) {
      try {
        const response = await masterAPI.getChaptersBySubject(formData.tradegroup, formData.trade, subjectId);
        if (response.data.success) {
          setAvailableChapters(response.data.data);
        }
      } catch (error) {
        console.error('Error fetching chapters:', error);
        setAvailableChapters([]);
      }
    } else {
      setAvailableChapters([]);
    }
  };

  const handleAddNew = () => {
    setEditingItem(null);
    setFormData({ name: '', tradegroup: '', trade: '', subject: '', chapter: '' });
    setAvailableTrades([]);
    setAvailableSubjects([]);
    setAvailableChapters([]);
    setShowModal(true);
  };

  const handleEdit = async (item) => {
    setEditingItem(item);
    setFormData({ 
      name: item.name, 
      tradegroup: item.tradegroup || '', 
      trade: item.trade || '',
      subject: item.subject || '',
      chapter: item.chapter || ''
    });
    
    if (item.tradegroup) {
      try {
        const tradesRes = await masterAPI.getTradesByGroup(item.tradegroup);
        if (tradesRes.data.success) {
          setAvailableTrades(tradesRes.data.data);
        }
        
        if (item.trade) {
          const subjectsRes = await masterAPI.getSubjectsByTrade(item.tradegroup, item.trade);
          if (subjectsRes.data.success) {
            setAvailableSubjects(subjectsRes.data.data);
          }
          
          if (item.subject) {
            const chaptersRes = await masterAPI.getChaptersBySubject(item.tradegroup, item.trade, item.subject);
            if (chaptersRes.data.success) {
              setAvailableChapters(chaptersRes.data.data);
            }
          }
        }
      } catch (error) {
        console.error('Error loading data:', error);
      }
    }
    
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      let response;
      if (editingItem) {
        response = await masterAPI.updateTopic(editingItem.id, formData);
      } else {
        response = await masterAPI.createTopic(formData);
      }

      if (response.data.success) {
        toast.success(response.data.message);
        setShowModal(false);
        setFormData({ name: '', tradegroup: '', trade: '', subject: '', chapter: '' });
        setAvailableTrades([]);
        setAvailableSubjects([]);
        setAvailableChapters([]);
        fetchTopics();
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Operation failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleStatus = async (item) => {
    const action = item.status === 1 ? 'disable' : 'enable';
    if (!window.confirm(`Are you sure you want to ${action} "${item.name}"?`)) {
      return;
    }

    try {
      const response = await masterAPI.disableTopic(item.id);
      if (response.data.success) {
        toast.success(response.data.message);
        fetchTopics();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Operation failed');
    }
  };

  // Filter topics based on search query
  const filteredTopics = useMemo(() => {
    if (!searchQuery.trim()) return topics;
    
    const query = searchQuery.toLowerCase();
    return topics.filter(item => {
      const name = item.name?.toLowerCase() || '';
      const tradegroupName = item.tradegroup_name?.toLowerCase() || '';
      const tradeName = item.trade_name?.toLowerCase() || '';
      const subjectName = item.subject_name?.toLowerCase() || '';
      const chapterName = item.chapter_name?.toLowerCase() || '';
      const createdBy = item.created_by_institute?.toLowerCase() || '';
      
      return name.includes(query) || tradegroupName.includes(query) || 
             tradeName.includes(query) || subjectName.includes(query) || 
             chapterName.includes(query) || createdBy.includes(query);
    });
  }, [topics, searchQuery]);

  if (loading) {
    return (
      <div className="flex justify-center items-center py-16">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="w-full">
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-xl font-semibold text-gray-900">Topics</h3>
        <button 
          className="flex items-center gap-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-lg transition-colors" 
          onClick={handleAddNew}
        >
          <FiPlus /> Add Topic
        </button>
      </div>

      {/* Search Bar */}
      <div className="mb-6">
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <FiSearch className="h-5 w-5 text-gray-400" />
          </div>
          <input
            type="text"
            placeholder="Search by name, category, trade, subject, chapter, or creator..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-2 focus:ring-primary-600 focus:border-transparent text-sm md:text-base"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute inset-y-0 right-0 pr-3 flex items-center"
            >
              <FiX className="h-5 w-5 text-gray-400 hover:text-gray-600" />
            </button>
          )}
        </div>
        {searchQuery && (
          <p className="mt-2 text-sm text-gray-600">
            Found {filteredTopics.length} {filteredTopics.length === 1 ? 'topic' : 'topics'}
          </p>
        )}
      </div>

      <div className="p-4 mb-5 bg-blue-50 border-l-4 border-blue-500 rounded-lg">
        <p className="text-sm text-blue-900">
          <strong>Note:</strong> Topics created by any institute are visible and usable by all institutes. 
          You can only edit/disable items created by your institute.
        </p>
      </div>

      {topics.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <FiList size={64} className="text-gray-300 mb-4" />
          <h3 className="text-xl font-semibold text-gray-700 mb-2">No topics yet</h3>
          <p className="text-gray-500 mb-6">Create your first topic to get started</p>
          <button 
            className="flex items-center gap-2 px-6 py-3 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-lg transition-colors" 
            onClick={handleAddNew}
          >
            <FiPlus /> Create Topic
          </button>
        </div>
      ) : filteredTopics.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 bg-gray-50 rounded-lg">
          <FiSearch size={48} className="text-gray-300 mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No results found</h3>
          <p className="text-sm text-gray-500 mb-6">
            No topics match "{searchQuery}". Try a different search term.
          </p>
          <button
            onClick={() => setSearchQuery('')}
            className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors"
          >
            Clear Search
          </button>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b-2 border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Category</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Trade</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Subject</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Chapter</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Name</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Created By</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Status</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredTopics.map((item) => {
                const canEdit = item.institute_id === user.institute_id || item.institute_id === null;
                const isDisabled = item.status === 0;
                
                return (
                  <tr 
                    key={item.id}
                    className={`border-b border-gray-100 hover:bg-gray-50 transition-colors ${
                      isDisabled ? 'opacity-50' : ''
                    }`}
                  >
                    <td className="px-4 py-3 text-sm text-gray-600">{item.tradegroup_name || 'N/A'}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{item.trade_name || 'N/A'}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{item.subject_name || 'N/A'}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{item.chapter_name || 'N/A'}</td>
                    <td className="px-4 py-3">
                      <span className="text-sm font-medium text-gray-900">{item.name}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs text-gray-600">{item.created_by_institute}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-block px-3 py-1 text-xs font-medium rounded-full ${
                        item.status === 1 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {item.status_label}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end items-center gap-2">
                        {canEdit && item.institute_id === user.institute_id && (
                          <>
                            <button
                              onClick={() => handleEdit(item)}
                              className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                              title="Edit"
                            >
                              <FiEdit2 size={16} />
                            </button>
                            <button
                              onClick={() => handleToggleStatus(item)}
                              className={`p-2 rounded-lg transition-colors ${
                                item.status === 1 
                                  ? 'text-red-600 hover:bg-red-50' 
                                  : 'text-green-600 hover:bg-green-50'
                              }`}
                              title={item.status === 1 ? 'Disable' : 'Enable'}
                            >
                              {item.status === 1 ? <FiToggleRight size={20} /> : <FiToggleLeft size={20} />}
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-900">
                {editingItem ? 'Edit Topic' : 'Add New Topic'}
              </h2>
              <button
                onClick={() => setShowModal(false)}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <FiX size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6">
              <div className="mb-5">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Category <span className="text-red-500">*</span>
                </label>
                <select
                  name="tradegroup"
                  value={formData.tradegroup}
                  onChange={handleTradeGroupChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600 focus:border-transparent outline-none transition-colors"
                  required
                >
                  <option value="">Select Category</option>
                  {tradeGroups.map((tg) => (
                    <option key={tg.id} value={tg.id}>{tg.name}</option>
                  ))}
                </select>
              </div>

              <div className="mb-5">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                 Sub Category<span className="text-red-500">*</span>
                </label>
                <select
                  name="trade"
                  value={formData.trade}
                  onChange={handleTradeChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600 focus:border-transparent outline-none transition-colors"
                  required
                  disabled={!formData.tradegroup}
                >
                  <option value="">Select Trade</option>
                  {availableTrades.map((t) => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </select>
              </div>

              <div className="mb-5">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Subject <span className="text-red-500">*</span>
                </label>
                <select
                  name="subject"
                  value={formData.subject}
                  onChange={handleSubjectChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600 focus:border-transparent outline-none transition-colors"
                  required
                  disabled={!formData.trade}
                >
                  <option value="">Select Subject</option>
                  {availableSubjects.map((s) => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
              </div>

              <div className="mb-5">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Chapter <span className="text-red-500">*</span>
                </label>
                <select
                  name="chapter"
                  value={formData.chapter}
                  onChange={(e) => setFormData({ ...formData, chapter: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600 focus:border-transparent outline-none transition-colors"
                  required
                  disabled={!formData.subject}
                >
                  <option value="">Select Chapter</option>
                  {availableChapters.map((ch) => (
                    <option key={ch.id} value={ch.id}>{ch.name}</option>
                  ))}
                </select>
              </div>

              <div className="mb-5">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Topic Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600 focus:border-transparent outline-none transition-colors"
                  placeholder="Enter topic name"
                  required
                />
                <small className="block mt-2 text-xs text-gray-500 italic">
                  This will be shared with all institutes
                </small>
              </div>

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="flex-1 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition-colors"
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-lg transition-colors"
                  disabled={submitting}
                >
                  {submitting ? 'Saving...' : (editingItem ? 'Update' : 'Create')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Masters;
